/**
 * JaanchApp v15 — CA Intelligence UI wired to live backend.
 *
 * Design: top nav primary (Today / Clients / Notices) + More menu.
 * Mobile: bottom tab bar.
 * Backend wiring: api.js CA endpoints.
 *
 * Maps UI surfaces → backend endpoints:
 *   Today       → getClientHealth, getComplianceCalendar, getNotices, getCAAgentStatus
 *   Clients     → listCAClients, onboardClient, sendWhatsAppReminder, sendBulkWhatsAppReminders
 *   Notices     → getNotices, uploadNotice, generateDocument (reply draft)
 *   GST         → runGSTReconciliation, getGSTReconHistory
 *   ITR         → prepareITR
 *   Compliance  → getComplianceCalendar, connectComplianceStream (SSE)
 *   Documents   → generateDocument (engagement_letter | computation_sheet | form_3cd)
 *   Ask CA Assist  → sendMessage (conversation), quickAsk
 *   Settings    → listConnectors, setupWhatsApp, testWhatsApp, getWhatsAppStatus, seedCAAgents
 */

import { useState, useEffect, useRef, useCallback } from "react";
import * as api from "./api.js";

const C = {
  bg: "#FAFAF7", surface: "#FFFFFF", surfaceAlt: "#F3F1EB", surfaceDim: "#EBE8E0",
  border: "#E4E0D6", borderStrong: "#CFC9B9",
  ink: "#0A0906", ink2: "#292520", ink3: "#4A4640",
  muted: "#807A70", faint: "#B0AA9E",
  signal: "#0F9E87", signalBg: "#E6F5F1", signalDeep: "#0B7D6B",
  red: "#C23B2E", redBg: "#FBEAE7", redDeep: "#8F2A20",
  amber: "#C47A0F", amberBg: "#FAF0DC",
  green: "#2E7D4F",
  night: "#12110F", nightAlt: "#1C1A16",
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
*{box-sizing:border-box;margin:0;padding:0;-webkit-font-smoothing:antialiased;-webkit-tap-highlight-color:transparent}
body,button,input,textarea,select{font-family:'Inter',sans-serif;font-size:14px;color:${C.ink};font-feature-settings:'cv11','ss01'}
.mono{font-family:'JetBrains Mono',monospace;font-feature-settings:'tnum','zero'}
.num{font-feature-settings:'tnum','lnum';font-variant-numeric:tabular-nums}
::-webkit-scrollbar{width:6px;height:6px}
::-webkit-scrollbar-thumb{background:${C.borderStrong};border-radius:3px}
@keyframes fade{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:translateY(0)}}
@keyframes tick{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.9)}}
@keyframes draw{to{stroke-dashoffset:0}}
@keyframes glow{0%,100%{box-shadow:0 0 0 0 rgba(15,158,135,0)}50%{box-shadow:0 0 24px -4px rgba(15,158,135,.25)}}
@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
@keyframes spin{to{transform:rotate(360deg)}}
.fade{animation:fade .35s cubic-bezier(.2,.9,.3,1) both}
.pulse{animation:pulse 1.8s ease infinite}
.glow{animation:glow 3s ease-in-out infinite}
.slideUp{animation:slideUp .25s cubic-bezier(.2,.9,.3,1) both}
.spin{animation:spin 1s linear infinite}
button{cursor:pointer;border:none;background:none;font-family:inherit}
[data-row]:hover{background:${C.surfaceAlt}}
.ticker-track{display:flex;gap:32px;animation:tick 60s linear infinite;width:max-content}
.ticker-track:hover{animation-play-state:paused}
`;

// ─── Primary nav (daily) + More (occasional) ────────────────────────────
const PRIMARY = [
  { id: "today",    label: "Today",    ico: "◐" },
  { id: "clients",  label: "Clients",  ico: "◎" },
  { id: "notices",  label: "Notices",  ico: "◬" },
];

const MORE = [
  { id: "gst",         label: "GST / ITC",  ico: "⊞" },
  { id: "itr",         label: "ITR / Tax",  ico: "⊟" },
  { id: "compliance",  label: "Compliance", ico: "◫" },
  { id: "documents",   label: "Documents",  ico: "◧" },
  { id: "ask",         label: "Ask CA Assist", ico: "✦" },
  { id: "settings",    label: "Settings",   ico: "⚙" },
];

// ─── Responsive hook ────────────────────────────────────────────────────
function useBP() {
  const [bp, setBp] = useState(() => {
    if (typeof window === "undefined") return { w: 1200, mob: false, tab: false };
    const w = window.innerWidth;
    return { w, mob: w < 720, tab: w < 1024 };
  });
  useEffect(() => {
    const on = () => { const w = window.innerWidth; setBp({ w, mob: w < 720, tab: w < 1024 }); };
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return bp;
}

// ─── Root ───────────────────────────────────────────────────────────────
export default function JaanchApp({ user, onLogout }) {
  const [page, setPage] = useState("today");
  const [selectedClient, setSelectedClient] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const bp = useBP();

  const go = (p, payload) => {
    setPage(p);
    if (p === "client-detail") setSelectedClient(payload);
    setMoreOpen(false);
    setSheetOpen(false);
  };
  const notify = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3200);
  };

  useEffect(() => {
    const onClick = () => setMoreOpen(false);
    if (moreOpen) {
      setTimeout(() => document.addEventListener("click", onClick), 0);
      return () => document.removeEventListener("click", onClick);
    }
  }, [moreOpen]);

  return (
    <>
      <style>{CSS}</style>
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: C.bg, overflow: "hidden" }}>
        <TopNav page={page} go={go} bp={bp} moreOpen={moreOpen} setMoreOpen={setMoreOpen} user={user} onLogout={onLogout} />
        <main style={{ flex: 1, overflowY: "auto", paddingBottom: bp.mob ? 62 : 0 }}>
          {page === "today"         && <TodayPage go={go} bp={bp} notify={notify} />}
          {page === "clients"       && <ClientsPage bp={bp} notify={notify} go={go} />}
          {page === "client-detail" && <ClientDetailPage client={selectedClient} bp={bp} notify={notify} back={() => go("clients")} />}
          {page === "notices"       && <NoticesPage bp={bp} notify={notify} />}
          {page === "gst"        && <GSTPage bp={bp} notify={notify} />}
          {page === "itr"        && <ITRPage bp={bp} notify={notify} />}
          {page === "compliance" && <CompliancePage bp={bp} notify={notify} />}
          {page === "documents"  && <DocumentsPage bp={bp} notify={notify} />}
          {page === "ask"        && <AskPage bp={bp} />}
          {page === "settings"   && <SettingsPage bp={bp} notify={notify} />}
        </main>
        {bp.mob && <BottomTabs page={page} go={go} setSheetOpen={setSheetOpen} />}
        {sheetOpen && <MoreSheet go={go} close={() => setSheetOpen(false)} page={page} />}
        {toast && <Toast toast={toast} />}
      </div>
    </>
  );
}

// ─── Top Navigation ─────────────────────────────────────────────────────
function TopNav({ page, go, bp, moreOpen, setMoreOpen, user, onLogout }) {
  const inMore = MORE.some((m) => m.id === page);
  const currentLabel = [...PRIMARY, ...MORE].find((n) => n.id === page)?.label || "CA Assist";

  return (
    <header style={{
      height: 52, background: C.surface, borderBottom: `1px solid ${C.border}`,
      display: "flex", alignItems: "center", padding: bp.mob ? "0 14px" : "0 20px",
      gap: bp.mob ? 12 : 22, flexShrink: 0, position: "relative", zIndex: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 7,
          background: `linear-gradient(135deg, ${C.signal}, ${C.signalDeep})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 14, fontWeight: 700, letterSpacing: "-.03em",
          boxShadow: `0 0 14px -2px ${C.signal}50`,
        }}>C</div>
        {!bp.mob && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1, color: C.ink }}>CA Assist</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{user?.org_name || "—"}</div>
          </div>
        )}
      </div>

      {!bp.mob && (
        <nav style={{ display: "flex", gap: 2, flex: 1 }}>
          {PRIMARY.map((it) => {
            const active = page === it.id;
            return (
              <button key={it.id} onClick={() => go(it.id)} style={{
                padding: "7px 13px", borderRadius: 6, fontSize: 13,
                display: "flex", alignItems: "center", gap: 7,
                background: active ? C.surfaceAlt : "transparent",
                color: active ? C.ink : C.ink3,
                fontWeight: active ? 500 : 400, position: "relative",
              }}>
                {it.label}
                {active && <span style={{ position: "absolute", bottom: -15, left: 10, right: 10, height: 2, background: C.signal, borderRadius: 2 }} />}
              </button>
            );
          })}

          <div style={{ position: "relative" }}>
            <button onClick={(e) => { e.stopPropagation(); setMoreOpen((o) => !o); }} style={{
              padding: "7px 13px", borderRadius: 6, fontSize: 13,
              background: moreOpen ? C.surfaceAlt : "transparent",
              color: inMore ? C.ink : C.ink3, fontWeight: inMore ? 500 : 400,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              More <span style={{ fontSize: 10, color: C.muted }}>▾</span>
            </button>
            {moreOpen && (
              <div onClick={(e) => e.stopPropagation()} className="fade" style={{
                position: "absolute", top: "calc(100% + 8px)", left: 0, minWidth: 200,
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
                boxShadow: "0 8px 24px rgba(10,9,6,.08)", padding: 4, zIndex: 20,
              }}>
                {MORE.map((m) => (
                  <button key={m.id} onClick={() => go(m.id)} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", fontSize: 13,
                    color: page === m.id ? C.ink : C.ink3, borderRadius: 5, textAlign: "left",
                    background: page === m.id ? C.surfaceAlt : "transparent",
                    fontWeight: page === m.id ? 500 : 400,
                  }}
                  onMouseEnter={(e) => { if (page !== m.id) e.currentTarget.style.background = C.surfaceAlt; }}
                  onMouseLeave={(e) => { if (page !== m.id) e.currentTarget.style.background = "transparent"; }}>
                    <span style={{ width: 14, color: C.muted }}>{m.ico}</span>
                    <span>{m.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>
      )}

      {bp.mob && <div style={{ flex: 1, fontSize: 14, fontWeight: 500, color: C.ink }}>{currentLabel}</div>}

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {!bp.mob && <SearchPill />}
        <LiveBadge mob={bp.mob} />
        <UserMenu user={user} onLogout={onLogout} />
      </div>
    </header>
  );
}

function SearchPill() {
  return (
    <button style={{
      display: "flex", alignItems: "center", gap: 8, padding: "5px 10px",
      background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6,
      fontSize: 12, color: C.muted, minWidth: 220,
    }}>
      <span style={{ fontSize: 12 }}>⌕</span>
      <span style={{ flex: 1, textAlign: "left" }}>Search clients, PAN, GSTIN…</span>
      <span className="mono" style={{ fontSize: 10, color: C.faint, padding: "1px 4px", background: C.surface, borderRadius: 3 }}>⌘K</span>
    </button>
  );
}

function LiveBadge({ mob }) {
  if (mob) return <span className="pulse" style={{ width: 7, height: 7, borderRadius: "50%", background: C.signal, display: "block" }} />;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", background: C.signalBg, borderRadius: 20 }}>
      <span className="pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: C.signal, display: "block" }} />
      <span style={{ fontSize: 11, color: C.signalDeep, fontWeight: 500, letterSpacing: 0.2 }}>Live</span>
    </div>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const initials = (user?.name || "RA").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        width: 28, height: 28, borderRadius: "50%",
        background: `linear-gradient(135deg, ${C.signal}, ${C.signalDeep})`,
        color: "#fff", fontSize: 11, fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>{initials}</button>
      {open && (
        <div className="fade" style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 180,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: "0 8px 24px rgba(10,9,6,.08)", padding: 4, zIndex: 30,
        }}>
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{user?.name || "CA"}</div>
            <div style={{ fontSize: 10, color: C.muted }}>{user?.email}</div>
          </div>
          <button onClick={onLogout} style={{
            width: "100%", padding: "8px 10px", fontSize: 12, color: C.ink3,
            borderRadius: 5, textAlign: "left",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.surfaceAlt; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Mobile bottom tabs + sheet ─────────────────────────────────────────
function BottomTabs({ page, go, setSheetOpen }) {
  const items = [...PRIMARY, { id: "__more", label: "More", ico: "⋯" }];
  return (
    <nav style={{
      position: "fixed", bottom: 0, left: 0, right: 0, height: 60,
      background: C.surface, borderTop: `1px solid ${C.border}`,
      display: "grid", gridTemplateColumns: "repeat(4, 1fr)", zIndex: 30,
      paddingBottom: "env(safe-area-inset-bottom)",
    }}>
      {items.map((it) => {
        const active = it.id === "__more" ? MORE.some((m) => m.id === page) : page === it.id;
        const onTap = () => (it.id === "__more" ? setSheetOpen(true) : go(it.id));
        return (
          <button key={it.id} onClick={onTap} style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
            color: active ? C.signalDeep : C.muted, position: "relative",
          }}>
            <span style={{ fontSize: 20 }}>{it.ico}</span>
            <span style={{ fontSize: 10, fontWeight: active ? 600 : 400 }}>{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function MoreSheet({ go, close, page }) {
  return (
    <>
      <div onClick={close} style={{ position: "fixed", inset: 0, background: "rgba(10,9,6,.5)", zIndex: 40, backdropFilter: "blur(3px)" }} />
      <div className="slideUp" style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 41,
        background: C.surface, borderRadius: "14px 14px 0 0", padding: "10px 0 24px",
        paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
      }}>
        <div style={{ width: 36, height: 4, background: C.borderStrong, borderRadius: 2, margin: "0 auto 12px" }} />
        <div style={{ padding: "0 18px 10px", fontSize: 11, color: C.muted, letterSpacing: 0.4, textTransform: "uppercase", fontWeight: 600 }}>More tools</div>
        {MORE.map((m) => (
          <button key={m.id} onClick={() => go(m.id)} style={{
            width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "13px 18px",
            fontSize: 15, color: page === m.id ? C.signalDeep : C.ink, textAlign: "left",
            background: page === m.id ? C.signalBg : "transparent",
          }}>
            <span style={{ width: 24, fontSize: 16, color: C.muted }}>{m.ico}</span>
            <span>{m.label}</span>
            <span style={{ flex: 1 }} />
            <span style={{ color: C.faint, fontSize: 16 }}>›</span>
          </button>
        ))}
      </div>
    </>
  );
}

// ─── Toast ──────────────────────────────────────────────────────────────
function Toast({ toast }) {
  const bg = toast.kind === "error" ? C.redBg : toast.kind === "warn" ? C.amberBg : C.signalBg;
  const fg = toast.kind === "error" ? C.redDeep : toast.kind === "warn" ? C.amber : C.signalDeep;
  return (
    <div className="fade" style={{
      position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
      background: bg, color: fg, padding: "10px 16px", borderRadius: 8,
      fontSize: 13, fontWeight: 500, border: `1px solid ${fg}30`, zIndex: 100,
      boxShadow: "0 4px 12px rgba(10,9,6,.08)", maxWidth: "90vw",
    }}>{toast.msg}</div>
  );
}

// ─── Today Page (home) ──────────────────────────────────────────────────
function TodayPage({ go, bp, notify }) {
  const [state, setState] = useState({ loading: true, health: null, notices: [], pulse: [], deadlines: [] });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [health, notices, cal] = await Promise.all([
          api.getClientHealth().catch(() => null),
          api.getNotices("all").catch(() => ({ notices: [] })),
          api.getComplianceCalendar().catch(() => null),
        ]);
        if (!mounted) return;

        const normalizedHealth = normalizeHealth(health);
        const normalizedCal = normalizeCalendar(cal);
        const normalizedNotices = (notices?.notices || []).map(normalizeNotice);
        const pulse = buildPulseFromHealth(normalizedHealth);

        setState({
          loading: false,
          health: normalizedHealth,
          notices: normalizedNotices,
          pulse,
          deadlines: normalizedCal.slice(0, 4),
        });
      } catch (e) {
        if (mounted) setState((s) => ({ ...s, loading: false }));
        notify("Couldn't load dashboard: " + (e.message || "unknown"), "error");
      }
    })();
    return () => { mounted = false; };
  }, []);

  const priority = state.notices.find((n) => n.status === "draft_ready") || state.notices[0];
  const attentionClients = (state.health?.clients || []).filter((c) => c.health !== "green");

  return (
    <div className="fade" style={{ padding: bp.mob ? 14 : "18px 22px", maxWidth: 1400, margin: "0 auto" }}>
      <ActivityTicker pulse={state.pulse} loading={state.loading} />

      {priority && (
        <div style={{ marginTop: 14 }}>
          <PriorityCard notice={priority} onClick={() => go("notices")} bp={bp} />
        </div>
      )}

      <StatsStrip health={state.health} notices={state.notices} bp={bp} loading={state.loading} />

      <div style={{ display: "grid", gridTemplateColumns: bp.tab ? "1fr" : "1fr 320px", gap: 14, marginTop: 14 }}>
        <ClientsAttentionCard clients={attentionClients} loading={state.loading} bp={bp} notify={notify} goClient={() => go("clients")} onOpenClient={(c) => go("client-detail", c)} />
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <DeadlinesCard deadlines={state.deadlines} loading={state.loading} />
          {!bp.mob && <PulseCard pulse={state.pulse} />}
        </div>
      </div>
    </div>
  );
}

/**
 * Normalize /ca/notices response items into UI shape.
 *
 * Backend returns: { document_id, title, snippet, source, pan, notice_type, section,
 *                    ay, issued_date, due_date, status, requires_response, demand_amount }
 * UI expects: { id, client_name, section, subject, amount, deadline, ref, status, received_date, pan }
 */
function normalizeNotice(raw) {
  if (!raw) return {};
  // Try to extract client name from title (e.g. "Notice u/s 143(1) — Verma Pharma")
  const titleParts = (raw.title || "").split(/—|-|:/);
  const clientGuess = titleParts.length > 1 ? titleParts.slice(1).join(" ").trim() : raw.title;

  const demand = raw.demand_amount || 0;
  const amount = demand > 0 ? demand.toLocaleString("en-IN") : null;

  // Has AI draft been generated? Use requires_response + existing fields as a heuristic.
  // Backend exposes status field as free text; treat anything with an actual draft string as ready.
  const hasDraft = !!raw.draft_text;
  const status = hasDraft ? "draft_ready" : (raw.status || (raw.requires_response ? "awaiting" : "resolved"));

  return {
    id: raw.document_id || raw.id,
    client_name: raw.client_name || clientGuess || "Unknown client",
    section: raw.section || raw.notice_type || "—",
    subject: raw.snippet || raw.title || "",
    amount,
    deadline: raw.due_date || "",
    received_date: raw.issued_date || "",
    ref: raw.reference || raw.document_id || "",
    pan: raw.pan || "",
    status,
    draft_text: raw.draft_text || null,
    evidence: raw.evidence || [],
    raw, // keep original for debugging
  };
}

function buildPulseFromHealth(health) {
  if (!health?.clients) return [];
  const events = [];
  (health.clients || []).slice(0, 6).forEach((c) => {
    (c.issues || []).slice(0, 1).forEach((iss) => {
      events.push({
        icon: "⚡", kind: "alert",
        text: `${iss} · ${c.name}`, when: "recent",
      });
    });
  });
  return events.length > 0 ? events : [{ icon: "✓", kind: "done", text: "All systems reconciled", when: "now" }];
}

/**
 * Normalize /ca/compliance/health response into a uniform shape.
 *
 * Backend returns: { alerts: { critical: [...], urgent: [...], ... }, health_report, ... }
 * UI expects: { clients: [{ name, health, issues: [...], gstin, pan, ... }] }
 */
function normalizeHealth(raw) {
  if (!raw) return { clients: [] };
  if (Array.isArray(raw.clients)) return raw;  // already in desired shape

  const alerts = raw.alerts || {};
  const map = new Map();

  const addAlert = (severity, alert) => {
    const key = alert.gstin || alert.pan || alert.client_name || alert.client || JSON.stringify(alert);
    const existing = map.get(key) || {
      id: key,
      name: alert.client_name || alert.client || "Unknown",
      gstin: alert.gstin || null,
      pan: alert.pan || null,
      entity_type: alert.entity_type || alert.type || null,
      phone: alert.phone || alert.contact_phone || null,
      health: "green",
      issues: [],
      next_deadline: alert.deadline || alert.due_date || null,
      itc_gap: alert.itc_gap || 0,
    };
    // Upgrade health severity if this alert is worse
    if (severity === "critical" || severity === "red") existing.health = "red";
    else if ((severity === "urgent" || severity === "amber") && existing.health !== "red") existing.health = "amber";

    const issueText = alert.issue || alert.description || alert.message || alert.title || severity;
    if (issueText) existing.issues.push(issueText);
    map.set(key, existing);
  };

  // Agent output commonly uses these buckets
  ["critical", "red", "urgent", "amber", "review", "warning"].forEach((bucket) => {
    const severity = (bucket === "critical" || bucket === "red") ? "critical" : "urgent";
    (alerts[bucket] || []).forEach((a) => addAlert(severity, a));
  });

  return { ...raw, clients: Array.from(map.values()) };
}

/**
 * Normalize /ca/compliance/calendar response into a uniform list of deadlines.
 *
 * Backend returns: { structured_calendar: { critical: [...], urgent: [...], upcoming: [...] }, ... }
 * UI expects: [{ form_type, due_date, urgent, label, sub }, ...]
 */
function normalizeCalendar(raw) {
  if (!raw) return [];
  if (Array.isArray(raw.items)) return raw.items;  // already desired shape

  const structured = raw.structured_calendar || {};
  const out = [];

  const push = (urgent, arr) => {
    (arr || []).forEach((item) => {
      out.push({
        form_type: item.form_type || item.filing || item.label || "Filing",
        due_date: item.due_date || item.deadline || item.date || "",
        clients_affected: item.clients_affected || item.clients || item.count || "",
        urgent,
        label: item.form_type || item.filing || item.label || "Filing",
        sub: item.due_date || item.deadline || "",
      });
    });
  };

  push(true,  structured.critical);
  push(true,  structured.urgent);
  push(false, structured.this_month);
  push(false, structured.upcoming);

  return out;
}

function ActivityTicker({ pulse, loading }) {
  const items = pulse.length > 0 ? [...pulse, ...pulse] : [];
  return (
    <div style={{ background: C.night, borderRadius: 7, padding: "9px 0", overflow: "hidden", position: "relative", border: "1px solid rgba(15,158,135,.15)", minHeight: 34 }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 72, zIndex: 2, background: `linear-gradient(90deg, ${C.night}, ${C.night} 40%, rgba(18,17,15,0))`, display: "flex", alignItems: "center", paddingLeft: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className="pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: C.signal, display: "block" }} />
          <span style={{ fontSize: 10, color: C.signal, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Pulse</span>
        </div>
      </div>
      <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 48, zIndex: 2, background: `linear-gradient(-90deg, ${C.night}, rgba(18,17,15,0))` }} />
      {items.length === 0 ? (
        <div style={{ paddingLeft: 90, fontSize: 12, color: "#6B655B" }}>
          {loading ? "Loading intelligence pulse…" : "Connect your first client in Settings → Connectors to start"}
        </div>
      ) : (
        <div className="ticker-track" style={{ paddingLeft: 90 }}>
          {items.map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 11, color: p.kind === "ai" ? C.signal : p.kind === "alert" ? "#E89B7E" : "#A3D9C5" }}>{p.icon}</span>
              <span style={{ fontSize: 12, color: "#D4CFBF" }}>{p.text}</span>
              <span style={{ fontSize: 10, color: "#6B655B" }}>{p.when}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PriorityCard({ notice, onClick, bp }) {
  const n = notice;
  return (
    <div onClick={onClick} className="glow" style={{
      background: `linear-gradient(135deg, ${C.night} 0%, ${C.nightAlt} 100%)`,
      borderRadius: 8, padding: bp.mob ? "14px 16px" : "18px 22px",
      cursor: "pointer", position: "relative", overflow: "hidden",
      border: "1px solid rgba(194,59,46,.3)",
    }}>
      <div style={{ position: "absolute", top: 0, right: 0, width: "40%", height: "100%", background: `radial-gradient(circle at top right, rgba(194,59,46,.15), transparent 70%)` }} />
      <div style={{ position: "relative", display: "flex", flexDirection: bp.mob ? "column" : "row", alignItems: bp.mob ? "flex-start" : "center", gap: bp.mob ? 12 : 16 }}>
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ padding: "2px 8px", background: "rgba(194,59,46,.2)", color: "#F29489", fontSize: 10, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", borderRadius: 3 }}>Priority</span>
          <span className="mono" style={{ fontSize: 10, color: "#857F72" }}>
            Due <span style={{ color: "#F29489", fontWeight: 600 }}>{n.deadline || "soon"}</span>
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: bp.mob ? 14 : 15, fontWeight: 500, color: "#F5F1E6", lineHeight: 1.4, marginBottom: 3 }}>
            Notice u/s {n.section} — {n.client_name}
            {n.amount && <> · <span className="mono" style={{ color: "#F29489" }}>₹{n.amount}</span></>}
          </div>
          <div style={{ fontSize: 11, color: "#8B8578", lineHeight: 1.4 }}>
            {n.status === "draft_ready" ? "AI draft ready · evidence sources reconciled" : "Awaiting documents from client"}
          </div>
        </div>
        <button style={{
          padding: "7px 14px", background: `linear-gradient(135deg, ${C.signal}, ${C.signalDeep})`,
          color: "#fff", borderRadius: 5, fontSize: 12, fontWeight: 500, flexShrink: 0,
          boxShadow: `0 0 12px -2px ${C.signal}70`,
        }}>Review →</button>
      </div>
    </div>
  );
}

function StatsStrip({ health, notices, bp, loading }) {
  const clients = health?.clients || [];
  const attention = clients.filter((c) => c.health === "red" || c.health === "amber").length;
  const activeNotices = notices.filter((n) => n.status !== "resolved").length;
  const totalITCFlagged = clients.reduce((s, c) => s + (c.itc_gap || 0), 0);

  const stats = [
    { label: "Clients",        value: clients.length, color: C.ink },
    { label: "Attention",      value: attention, color: C.red },
    { label: "Notices",        value: activeNotices, color: C.amber },
    { label: "Due this week",  value: 0, color: C.ink },
    { label: "ITC flagged",    value: totalITCFlagged > 0 ? `₹${(totalITCFlagged / 100000).toFixed(1)}L` : "—", color: C.amber, isText: true },
    { label: "Staff queue",    value: 0, color: C.ink },
  ];

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: bp.mob ? "repeat(2, 1fr)" : bp.tab ? "repeat(3, 1fr)" : "repeat(6, 1fr)",
      gap: 1, background: C.border, marginTop: 14, borderRadius: 7, overflow: "hidden", border: `1px solid ${C.border}`,
    }}>
      {stats.map((s, i) => <StatCell key={i} s={s} delay={i * 60} loading={loading} />)}
    </div>
  );
}

function StatCell({ s, delay, loading }) {
  const [val, setVal] = useState(s.isText ? s.value : 0);
  useEffect(() => {
    if (s.isText) { setVal(s.value); return; }
    const target = typeof s.value === "number" ? s.value : 0;
    const start = Date.now() + delay;
    const dur = 700;
    const id = setInterval(() => {
      const now = Date.now();
      if (now < start) return;
      const p = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * e));
      if (p >= 1) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [s.value, s.isText, delay]);

  return (
    <div style={{ background: C.surface, padding: "12px 14px 10px", minHeight: 68 }}>
      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600, marginBottom: 5 }}>{s.label}</div>
      {loading ? (
        <div style={{ width: 40, height: 22, background: C.surfaceAlt, borderRadius: 4 }} className="pulse" />
      ) : (
        <span className="num" style={{ fontSize: 22, fontWeight: 600, color: s.color, letterSpacing: "-.02em", lineHeight: 1 }}>{val}</span>
      )}
    </div>
  );
}

function ClientsAttentionCard({ clients, loading, bp, notify, goClient, onOpenClient }) {
  return (
    <section style={{ background: C.surface, borderRadius: 7, overflow: "hidden", border: `1px solid ${C.border}` }}>
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>Clients needing attention</h3>
          {!loading && <span style={{ fontSize: 11, color: C.muted }}>{clients.length}</span>}
        </div>
      </div>
      <div>
        {loading && <LoadingRows count={3} />}
        {!loading && clients.length === 0 && (
          <div style={{ padding: "30px 20px", textAlign: "center", color: C.muted, fontSize: 13 }}>
            No clients need attention. ✓
          </div>
        )}
        {!loading && clients.slice(0, 5).map((c, i) => <ClientRow key={c.id || i} c={c} bp={bp} notify={notify} delay={i * 40} onOpen={onOpenClient} />)}
      </div>
      {!loading && clients.length > 0 && (
        <div style={{ padding: "8px 14px", borderTop: `1px solid ${C.border}`, background: C.surfaceAlt }}>
          <button onClick={goClient} style={{ fontSize: 12, color: C.signalDeep, fontWeight: 500 }}>View all clients →</button>
        </div>
      )}
    </section>
  );
}

function LoadingRows({ count = 3 }) {
  return Array.from({ length: count }).map((_, i) => (
    <div key={i} style={{ padding: "14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12 }}>
      <div className="pulse" style={{ width: 7, height: 7, borderRadius: "50%", background: C.borderStrong }} />
      <div style={{ flex: 1 }}>
        <div className="pulse" style={{ width: "40%", height: 12, background: C.surfaceAlt, borderRadius: 3, marginBottom: 4 }} />
        <div className="pulse" style={{ width: "60%", height: 10, background: C.surfaceAlt, borderRadius: 3 }} />
      </div>
    </div>
  ));
}

function ClientRow({ c, bp, notify, delay = 0, onOpen }) {
  const [sending, setSending] = useState(false);
  const h = c.health || (c.issues?.length > 0 ? "amber" : "green");
  const dotColor = h === "red" ? C.red : h === "amber" ? C.amber : C.green;
  const firstIssue = c.issues?.[0] || "—";

  const sendWA = async (e) => {
    e.stopPropagation();
    if (!c.gstin) { notify("No GSTIN — client on IT Portal only", "warn"); return; }
    if (!c.phone) { notify("No phone number on file", "warn"); return; }
    setSending(true);
    try {
      await api.sendWhatsAppReminder(c.gstin, c.phone, `Reminder regarding: ${firstIssue}`, c.name);
      notify(`WhatsApp sent to ${c.name}`);
    } catch (err) {
      notify(err.message || "WhatsApp failed — check Meta setup in Settings", "error");
    } finally {
      setSending(false);
    }
  };

  const handleOpen = () => { if (onOpen) onOpen(c); };

  return (
    <div data-row className="fade" onClick={handleOpen} style={{
      padding: "11px 14px", borderBottom: `1px solid ${C.border}`, cursor: "pointer",
      display: "grid",
      gridTemplateColumns: bp.mob ? "auto 1fr auto" : "14px 1fr 1fr 100px 80px 20px",
      gap: bp.mob ? 10 : 14, alignItems: "center", animationDelay: `${delay}ms`,
    }}>
      <div style={{ width: 7, height: 7, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
        <div style={{ fontSize: 11, color: C.muted, display: "flex", gap: 6, alignItems: "center", marginTop: 1 }}>
          {c.entity_type && <><span>{c.entity_type}</span><span>·</span></>}
          <span className="mono">{c.gstin ? c.gstin.slice(0, 10) + "…" : c.pan}</span>
          {bp.mob && firstIssue !== "—" && <><span>·</span><span style={{ color: dotColor, fontWeight: 500 }}>{firstIssue}</span></>}
        </div>
      </div>
      {!bp.mob && <div style={{ fontSize: 12, color: dotColor, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{firstIssue}</div>}
      {!bp.mob && <div style={{ textAlign: "right" }}>
        <button onClick={sendWA} disabled={sending} style={{
          fontSize: 10, padding: "3px 8px", borderRadius: 4,
          background: sending ? C.surfaceAlt : C.signalBg, color: C.signalDeep, fontWeight: 500,
        }}>{sending ? "…" : "📱 Remind"}</button>
      </div>}
      {!bp.mob && <div style={{ textAlign: "right" }}>
        <div className="mono" style={{ fontSize: 11, fontWeight: 500, color: C.ink2 }}>{c.next_deadline || "—"}</div>
      </div>}
      <div style={{ color: C.faint, fontSize: 14, textAlign: "right" }}>›</div>
    </div>
  );
}

function DeadlinesCard({ deadlines, loading }) {
  return (
    <section style={{ background: C.surface, borderRadius: 7, overflow: "hidden", border: `1px solid ${C.border}` }}>
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
        <h3 style={{ fontSize: 13, fontWeight: 600 }}>Today &amp; this week</h3>
      </div>
      {loading && <LoadingRows count={3} />}
      {!loading && deadlines.length === 0 && (
        <div style={{ padding: "24px 14px", textAlign: "center", color: C.muted, fontSize: 12 }}>
          No upcoming deadlines
        </div>
      )}
      {!loading && deadlines.map((d, i) => (
        <div key={i} style={{ padding: "10px 14px", borderBottom: i < deadlines.length - 1 ? `1px solid ${C.border}` : "none", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ width: 3, height: 28, borderRadius: 2, background: d.urgent ? C.red : C.borderStrong, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: C.ink, lineHeight: 1.35 }}>{d.form_type || d.label || "Filing"}</div>
            <div style={{ fontSize: 11, color: d.urgent ? C.red : C.muted, marginTop: 2 }}>{d.due_date || d.sub || ""}</div>
          </div>
        </div>
      ))}
    </section>
  );
}

function PulseCard({ pulse }) {
  return (
    <section style={{ background: C.surface, borderRadius: 7, overflow: "hidden", border: `1px solid ${C.border}` }}>
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h3 style={{ fontSize: 13, fontWeight: 600 }}>Intelligence pulse</h3>
        <span className="pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: C.signal, display: "block" }} />
      </div>
      <div style={{ padding: "4px 0" }}>
        {pulse.length === 0 ? (
          <div style={{ padding: "20px 14px", fontSize: 12, color: C.muted, textAlign: "center" }}>No activity yet</div>
        ) : pulse.slice(0, 5).map((p, i) => (
          <div key={i} style={{ padding: "7px 14px", display: "flex", alignItems: "flex-start", gap: 9 }}>
            <span style={{ fontSize: 11, marginTop: 2, color: p.kind === "ai" ? C.signal : p.kind === "alert" ? C.amber : C.green }}>{p.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: C.ink2, lineHeight: 1.4 }}>{p.text}</div>
              <div style={{ fontSize: 10, color: C.faint, marginTop: 1 }}>{p.when}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Clients Page ───────────────────────────────────────────────────────
// ─── Client Detail Page ─────────────────────────────────────────────────
function ClientDetailPage({ client, bp, notify, back }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wa, setWa] = useState(false);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    const id = client.gstin || client.pan;
    if (!id) { setLoading(false); notify("No GSTIN or PAN — can't load summary", "warn"); return; }
    api.getClientSummary(encodeURIComponent(id))
      .then((r) => setSummary(r))
      .catch((e) => notify("Couldn't load details: " + e.message, "error"))
      .finally(() => setLoading(false));
  }, [client]);

  if (!client) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: C.muted }}>
        No client selected. <button onClick={back} style={{ color: C.signalDeep, textDecoration: "underline" }}>Back to clients</button>
      </div>
    );
  }

  const h = client.health || (client.issues?.length > 0 ? "amber" : "green");
  const dotColor = h === "red" ? C.red : h === "amber" ? C.amber : C.green;

  const sendWA = async () => {
    if (!client.gstin) { notify("No GSTIN on file", "warn"); return; }
    if (!client.phone) { notify("No phone number on file", "warn"); return; }
    setWa(true);
    try {
      await api.sendWhatsAppReminder(client.gstin, client.phone, "Reminder from your CA", client.name);
      notify(`WhatsApp sent to ${client.name}`);
    } catch (e) { notify(e.message || "WhatsApp failed", "error"); }
    setWa(false);
  };

  return (
    <div className="fade" style={{ padding: bp.mob ? 14 : "18px 22px", maxWidth: 1200, margin: "0 auto" }}>
      <button onClick={back} style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>‹ Back to clients</button>

      <div style={{ background: C.surface, borderRadius: 7, border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 14 }}>
        <div style={{ padding: bp.mob ? "14px 16px" : "18px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor, marginTop: 6, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ fontSize: bp.mob ? 18 : 22, fontWeight: 600, color: C.ink, letterSpacing: "-.01em" }}>{client.name}</h1>
            <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap", fontSize: 11, color: C.muted }}>
              {client.entity_type && <span>{client.entity_type}</span>}
              {client.gstin && <><span>·</span><span className="mono">GSTIN: {client.gstin}</span></>}
              {client.pan && <><span>·</span><span className="mono">PAN: {client.pan}</span></>}
              {client.phone && <><span>·</span><span>{client.phone}</span></>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <Btn onClick={sendWA} disabled={wa}>{wa ? "…" : "📱 Send reminder"}</Btn>
            <Btn primary onClick={() => notify("Opening sync…")}>Sync now</Btn>
          </div>
        </div>

        {client.issues?.length > 0 && (
          <div style={{ padding: "12px 22px", background: C.redBg + "40", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 10, color: C.redDeep, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 6 }}>
              Open issues · {client.issues.length}
            </div>
            {client.issues.map((iss, i) => (
              <div key={i} style={{ fontSize: 12, color: C.ink2, lineHeight: 1.6, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: dotColor, marginTop: 2 }}>●</span>
                <span>{iss}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: bp.tab ? "1fr" : "1fr 1fr", gap: 14 }}>
        <section style={{ background: C.surface, borderRadius: 7, border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
            <h3 style={{ fontSize: 13, fontWeight: 600 }}>Data sources</h3>
          </div>
          {loading ? <LoadingRows count={3} /> : (
            <div>
              {(summary?.connectors || ["Tally Prime", "GST Portal", "IT Portal"]).map((src, i) => (
                <div key={i} style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, fontSize: 12, display: "flex", justifyContent: "space-between" }}>
                  <span>{typeof src === "string" ? src : src.name}</span>
                  <span style={{ color: C.signalDeep, fontWeight: 500 }}>● Connected</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section style={{ background: C.surface, borderRadius: 7, border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
            <h3 style={{ fontSize: 13, fontWeight: 600 }}>Upcoming deadlines</h3>
          </div>
          {loading ? <LoadingRows count={3} /> : (
            <div>
              {(summary?.deadlines || []).length === 0 && (
                <div style={{ padding: "20px 14px", textAlign: "center", color: C.muted, fontSize: 12 }}>No upcoming deadlines</div>
              )}
              {(summary?.deadlines || []).map((d, i) => (
                <div key={i} style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, fontSize: 12, display: "flex", justifyContent: "space-between" }}>
                  <span>{d.form_type || d.label}</span>
                  <span className="mono" style={{ color: C.muted }}>{d.due_date}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {summary?.raw && (
        <details style={{ marginTop: 14 }}>
          <summary style={{ fontSize: 11, color: C.muted, cursor: "pointer" }}>Debug: raw response</summary>
          <pre style={{ fontSize: 10, background: C.surfaceAlt, padding: 10, borderRadius: 5, overflow: "auto", marginTop: 6 }}>
            {JSON.stringify(summary, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function ClientsPage({ bp, notify, go }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listCAClients();
      setClients((res?.clients || []).map(c => ({...c, name: c.client_name || c.name || c.gstin})));
    } catch (e) {
      notify("Couldn't load clients: " + e.message, "error");
    }
    setLoading(false);
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? clients : clients.filter((c) => {
    const h = c.health || (c.issues?.length > 0 ? "amber" : "green");
    return filter === h;
  });

  const counts = {
    all: clients.length,
    red: clients.filter((c) => c.health === "red").length,
    amber: clients.filter((c) => c.health === "amber").length,
    green: clients.filter((c) => !c.health || c.health === "green").length,
  };

  const bulkRemind = async () => {
    try {
      const r = await api.sendBulkWhatsAppReminders();
      notify(`Queued ${r?.queued || 0} WhatsApp reminders`);
    } catch (e) {
      notify(e.message || "Bulk reminder failed — check WhatsApp setup", "error");
    }
  };

  return (
    <div className="fade" style={{ padding: bp.mob ? 14 : "18px 22px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ background: C.surface, borderRadius: 7, overflow: "hidden", border: `1px solid ${C.border}` }}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6, overflowX: "auto" }}>
            <Filter active={filter === "all"}   onClick={() => setFilter("all")}>All ({counts.all})</Filter>
            <Filter active={filter === "red"}   onClick={() => setFilter("red")}>Critical ({counts.red})</Filter>
            <Filter active={filter === "amber"} onClick={() => setFilter("amber")}>Review ({counts.amber})</Filter>
            <Filter active={filter === "green"} onClick={() => setFilter("green")}>Clear ({counts.green})</Filter>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {!bp.mob && <Btn onClick={bulkRemind}>Bulk WhatsApp</Btn>}
            <Btn primary onClick={() => setWizardOpen(true)}>+ Add Client</Btn>
          </div>
        </div>
        {loading && <LoadingRows count={4} />}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: C.muted, fontSize: 13 }}>
            {clients.length === 0 ? "No clients yet — add your first client to begin" : "No clients match this filter"}
          </div>
        )}
        {!loading && filtered.map((c, i) => <ClientRow key={c.id || i} c={c} bp={bp} notify={notify} delay={i * 30} onOpen={(client) => go("client-detail", client)} />)}
      </div>
      {wizardOpen && <AddClientWizard close={() => { setWizardOpen(false); load(); }} notify={notify} bp={bp} />}
    </div>
  );
}

function Filter({ children, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontSize: 11, padding: "3px 9px", borderRadius: 4,
      background: active ? C.ink : "transparent", color: active ? "#fff" : C.ink3,
      border: active ? "none" : `1px solid ${C.border}`,
      fontWeight: active ? 500 : 400, whiteSpace: "nowrap",
    }}>{children}</button>
  );
}

function Btn({ children, primary, fullWidth, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "6px 11px", fontSize: 11, fontWeight: 500,
      background: disabled ? C.surfaceAlt : primary ? C.ink : C.surface,
      color: disabled ? C.faint : primary ? "#fff" : C.ink2,
      border: primary ? "none" : `1px solid ${C.border}`,
      borderRadius: 5, width: fullWidth ? "100%" : "auto", whiteSpace: "nowrap",
      cursor: disabled ? "default" : "pointer",
    }}>{children}</button>
  );
}

// ─── Add Client Wizard (4 steps, wired) ─────────────────────────────────
function AddClientWizard({ close, notify, bp }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [d, setD] = useState({
    client_name: "", pan: "", gstin: "", entity_type: "company",
    contact_phone: "", contact_email: "",
    tally_enabled: false, tally_url: "",
    gst_enabled: false, it_enabled: false, traces_enabled: false,
    financial_year: "2024-25",
  });
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  const valid1 = d.client_name.trim().length > 0 && /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(d.pan);

  const submit = async () => {
    setSaving(true);
    try {
      await api.onboardClient(d);
      notify(`Client ${d.client_name} added — initial sync running`);
      close();
    } catch (e) {
      notify(e.message || "Couldn't add client", "error");
      setSaving(false);
    }
  };

  return (
    <Modal title="Add Client" close={close} bp={bp}>
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {[1, 2, 3, 4].map((n) => (
          <div key={n} style={{ flex: 1, height: 3, borderRadius: 2, background: n <= step ? C.signal : C.border, transition: "background .2s" }} />
        ))}
      </div>

      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Field label="Client name *" v={d.client_name} set={(v) => set("client_name", v)} ph="Sharma Trading Co." />
          <Field label="PAN *"   v={d.pan}   set={(v) => set("pan", v.toUpperCase())} ph="AABCS1429B" mono maxLen={10} />
          <Field label="GSTIN"   v={d.gstin} set={(v) => set("gstin", v.toUpperCase())} ph="27AABCS1429B1ZS" mono maxLen={15} />
          <label style={{ display: "block" }}>
            <div style={{ fontSize: 11, color: C.ink2, fontWeight: 500, marginBottom: 4 }}>Entity type</div>
            <select value={d.entity_type} onChange={(e) => set("entity_type", e.target.value)} style={inputStyle}>
              <option value="company">Pvt Ltd Company</option>
              <option value="llp">LLP</option>
              <option value="partnership">Partnership Firm</option>
              <option value="huf">HUF</option>
              <option value="individual">Individual</option>
              <option value="trust">Trust</option>
            </select>
          </label>
          <Field label="Contact phone" v={d.contact_phone} set={(v) => set("contact_phone", v)} ph="+91 98200 14291" />
          <Field label="Contact email" v={d.contact_email} set={(v) => set("contact_email", v)} ph="contact@company.com" />
        </div>
      )}

      {step === 2 && (
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px", background: C.surfaceAlt, borderRadius: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={d.tally_enabled} onChange={(e) => set("tally_enabled", e.target.checked)} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Connect Tally Prime</div>
              <div style={{ fontSize: 11, color: C.muted }}>Syncs vouchers, ledgers, GST data every 6 hours</div>
            </div>
          </label>
          {d.tally_enabled && (
            <div style={{ marginTop: 12 }}>
              <Field label="Tally HTTP URL *" v={d.tally_url} set={(v) => set("tally_url", v)} ph="http://192.168.1.10:9000" mono />
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                On the Tally machine: Gateway → F12 → Advanced Config → Enable TDL + HTTP port 9000
              </div>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Enable portals to sync; credentials go in Settings.</div>
          {[
            { k: "gst_enabled",    l: "GST Portal",    d: "GSTR-1, 2A, 2B, 3B, 9 via GSP" },
            { k: "it_enabled",     l: "Income Tax",    d: "26AS, AIS, ITR, notices via ERI" },
            { k: "traces_enabled", l: "TRACES",        d: "24Q, 26Q, Form 16, TDS defaults" },
          ].map((p) => (
            <label key={p.k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: C.surfaceAlt, borderRadius: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={d[p.k]} onChange={(e) => set(p.k, e.target.checked)} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.l}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{p.d}</div>
              </div>
            </label>
          ))}
        </div>
      )}

      {step === 4 && (
        <div>
          <div style={{ background: C.surfaceAlt, borderRadius: 7, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Summary</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 3 }}>{d.client_name || "(unnamed)"}</div>
            <div className="mono" style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>{d.pan} · {d.entity_type}</div>
            <div style={{ fontSize: 11, color: C.ink3 }}>
              Connectors: {[d.tally_enabled && "Tally", d.gst_enabled && "GST", d.it_enabled && "IT", d.traces_enabled && "TRACES"].filter(Boolean).join(", ") || "None"}
            </div>
          </div>
          <div style={{ padding: "10px 12px", background: C.signalBg, borderRadius: 6, fontSize: 12, color: C.signalDeep, lineHeight: 1.6 }}>
            On confirm, initial sync runs in background. First data available in 2–5 minutes.
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
        {step > 1 && <Btn onClick={() => setStep(step - 1)}>Back</Btn>}
        {step < 4 && <Btn primary fullWidth onClick={() => setStep(step + 1)} disabled={step === 1 && !valid1}>Continue</Btn>}
        {step === 4 && <Btn primary fullWidth onClick={submit} disabled={saving}>{saving ? "Adding…" : "Confirm & Add"}</Btn>}
      </div>
    </Modal>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 10px", fontSize: 13,
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 5,
  color: C.ink, outline: "none", fontFamily: "inherit",
};

function Field({ label, v, set, ph, mono, maxLen }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, color: C.ink2, fontWeight: 500, marginBottom: 4 }}>{label}</div>
      <input value={v} onChange={(e) => set(e.target.value)} placeholder={ph} maxLength={maxLen}
        style={{ ...inputStyle, fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit" }} />
    </label>
  );
}

function Modal({ title, close, children, bp, wide }) {
  return (
    <div onClick={close} style={{
      position: "fixed", inset: 0, background: "rgba(10,9,6,.55)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: bp.mob ? "flex-end" : "center", justifyContent: "center",
      zIndex: 100, padding: bp.mob ? 0 : 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} className={bp.mob ? "slideUp" : "fade"} style={{
        width: "100%", maxWidth: wide ? 640 : 520, maxHeight: bp.mob ? "94vh" : "90vh",
        background: C.surface, borderRadius: bp.mob ? "14px 14px 0 0" : 10,
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 24px 64px rgba(10,9,6,.25)",
      }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button onClick={close} style={{ color: C.muted, fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Notices Page ───────────────────────────────────────────────────────
function NoticesPage({ bp, notify }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getNotices("all");
      const list = (res?.notices || []).map(normalizeNotice);
      setNotices(list);
      if (!selected && list.length > 0 && !bp.mob) setSelected(list[0]);
    } catch (e) { notify("Couldn't load notices: " + e.message, "error"); }
    setLoading(false);
  }, [bp.mob, selected, notify]);

  useEffect(() => { load(); }, []);

  if (bp.mob && selected) {
    return (
      <div className="fade" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, background: C.surface, flexShrink: 0 }}>
          <button onClick={() => setSelected(null)} style={{ fontSize: 12, color: C.muted }}>‹ Back</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}><NoticeDetail notice={selected} bp={bp} notify={notify} /></div>
      </div>
    );
  }

  const list = (
    <NoticeList notices={notices} loading={loading} setSelected={setSelected} selected={selected} bp={bp} onUpload={() => setUploadOpen(true)} />
  );

  return (
    <>
      {bp.mob ? list : (
        <div className="fade" style={{ display: "grid", gridTemplateColumns: "340px 1fr", height: "100%" }}>
          {list}
          <div style={{ overflowY: "auto" }}>
            {selected ? <NoticeDetail notice={selected} bp={bp} notify={notify} /> : <EmptyState msg="Select a notice to view" />}
          </div>
        </div>
      )}
      {uploadOpen && <UploadNoticeModal
        close={() => { setUploadOpen(false); load(); }}
        notify={notify} bp={bp}
        onUploaded={(notice) => {
          // Add the freshly processed notice to top of list and select it
          const normalized = normalizeNotice(notice);
          setNotices(prev => [normalized, ...prev]);
          setSelected(normalized);
          setUploadOpen(false);
        }}
      />}
    </>
  );
}

function NoticeList({ notices, loading, setSelected, selected, bp, onUpload }) {
  return (
    <aside style={{
      borderRight: bp.mob ? "none" : `1px solid ${C.border}`,
      background: C.surfaceAlt, overflowY: "auto", height: bp.mob ? "100%" : "auto",
    }}>
      <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 600 }}>Active notices</h3>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {loading ? "Loading…" : `${notices.length} pending`}
          </div>
        </div>
        <Btn onClick={onUpload}>+ Upload PDF</Btn>
      </div>
      {loading && <LoadingRows count={3} />}
      {!loading && notices.length === 0 && (
        <div style={{ padding: "30px 20px", textAlign: "center", color: C.muted, fontSize: 12 }}>No notices yet</div>
      )}
      {!loading && notices.map((n) => (
        <div key={n.id} onClick={() => setSelected(n)} style={{
          padding: "12px 14px", borderBottom: `1px solid ${C.border}`,
          background: selected?.id === n.id ? C.surface : "transparent",
          borderLeft: selected?.id === n.id ? `2px solid ${C.signal}` : "2px solid transparent",
          cursor: "pointer",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, fontWeight: 600, background: C.redBg, color: C.red }}>u/s {n.section}</span>
            {n.status === "draft_ready" && <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, fontWeight: 600, background: C.signalBg, color: C.signalDeep }}>✦ Draft ready</span>}
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 2 }}>{n.client_name}</div>
          <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4, marginBottom: 7 }}>{n.subject || "—"}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="mono" style={{ fontSize: 10, color: C.muted, fontWeight: 500 }}>Due {n.deadline || "—"}</span>
            {n.amount && <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: C.red }}>₹{n.amount}</span>}
          </div>
        </div>
      ))}
    </aside>
  );
}

function NoticeDetail({ notice, bp, notify }) {
  const pad = bp.mob ? "16px 14px" : "22px 28px";
  return (
    <div style={{ padding: pad }} key={notice.id}>
      <div className="fade">
        <div style={{ paddingBottom: 16, borderBottom: `1px solid ${C.border}`, marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 3, fontWeight: 600, background: C.redBg, color: C.red }}>Section {notice.section}</span>
            {notice.ref && <span className="mono" style={{ fontSize: 11, color: C.muted }}>{notice.ref}</span>}
          </div>
          <h1 style={{ fontSize: bp.mob ? 17 : 20, fontWeight: 600, color: C.ink, marginBottom: 5, letterSpacing: "-.01em" }}>{notice.client_name}</h1>
          <div style={{ fontSize: 13, color: C.ink3, marginBottom: 16, lineHeight: 1.5 }}>{notice.subject || "—"}</div>
          <div style={{ display: "grid", gridTemplateColumns: bp.mob ? "repeat(2, 1fr)" : "repeat(4, auto)", gap: bp.mob ? "10px 20px" : "0 36px" }}>
            <Meta label="Received" value={notice.received_date || "—"} />
            <Meta label="Deadline" value={notice.deadline || "—"} highlight />
            <Meta label="Demand"   value={notice.amount ? `₹${notice.amount}` : "—"} mono />
            <Meta label="PAN"      value={notice.pan || "—"} mono />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: bp.tab ? "1fr" : "1fr 300px", gap: 24 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="pulse" style={{ width: 7, height: 7, borderRadius: "50%", background: notice.status === "draft_ready" ? C.signal : C.amber, display: "block" }} />
                <span style={{ fontSize: 11, color: notice.status === "draft_ready" ? C.signalDeep : C.amber, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>
                  {notice.status === "draft_ready" ? "AI draft ready" : "Awaiting data"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Btn onClick={() => notify("Regenerating draft…")}>Regenerate</Btn>
                <Btn primary onClick={() => { navigator.clipboard?.writeText(notice.draft_text || ""); notify("Copied"); }}>Copy</Btn>
              </div>
            </div>
            <article style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7,
              padding: bp.mob ? "18px 20px" : "26px 30px",
              fontSize: 13, lineHeight: 1.7, color: C.ink2, whiteSpace: "pre-wrap",
            }}>
              {notice.draft_text || "Draft not yet available. Upload the notice PDF or wait for AI to process."}
            </article>
          </div>

          <aside>
            <div style={{ fontSize: 10, color: C.muted, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span>Evidence · {notice.evidence?.length || 0} sources</span>
              <span style={{ height: 1, background: C.border, flex: 1 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {(notice.evidence || []).map((e, i) => (
                <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "10px 12px", borderLeft: `2px solid ${C.signal}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{e.source}</div>
                    <div style={{ fontSize: 9, color: C.signalDeep, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{e.tag}</div>
                  </div>
                  <div className="mono" style={{ fontSize: 10, color: C.ink3, lineHeight: 1.4 }}>{e.detail}</div>
                </div>
              ))}
              {(notice.evidence || []).length === 0 && (
                <div style={{ padding: 14, background: C.surfaceAlt, borderRadius: 6, fontSize: 11, color: C.muted, textAlign: "center" }}>
                  Evidence loading as connectors sync…
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function UploadNoticeModal({ close, notify, bp, onUploaded }) {
  const [file, setFile] = useState(null);
  const [clientName, setClientName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(""); // status messages during AI processing
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setProgress("Extracting text from PDF…");
    try {
      setProgress("Reading notice — classifying section and demand…");
      const r = await api.uploadNotice(file, clientName || "Unknown client");
      setProgress("Cross-referencing client records…");
      await new Promise(res => setTimeout(res, 600)); // brief pause so user sees progress
      notify("Notice processed — AI draft ready");
      if (onUploaded && r) onUploaded(r);
      close();
    } catch (e) {
      notify(e.message || "Upload failed", "error");
      setUploading(false);
      setProgress("");
    }
  };

  return (
    <Modal title="Upload notice PDF" close={close} bp={bp}>
      <Field label="Client name (optional — AI will try to match)" v={clientName} set={setClientName} ph="Verma Pharma Pvt Ltd" />
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: C.ink2, fontWeight: 500, marginBottom: 4 }}>PDF file *</div>
        <div onClick={() => inputRef.current?.click()} style={{
          border: `2px dashed ${file ? C.signal : C.border}`, borderRadius: 8,
          padding: "30px 20px", textAlign: "center", cursor: "pointer",
          background: file ? C.signalBg : C.surfaceAlt,
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{file ? "✓" : "📤"}</div>
          <div style={{ fontSize: 13, fontWeight: 500, color: file ? C.signalDeep : C.ink }}>
            {file ? file.name : "Click to select PDF"}
          </div>
          {file && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{Math.round(file.size / 1024)} KB</div>}
        </div>
        <input ref={inputRef} type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0])} style={{ display: "none" }} />
      </div>
      {uploading && progress && (
        <div style={{ marginTop: 10, padding: "8px 12px", background: C.signalBg, borderRadius: 6, fontSize: 12, color: C.signalDeep, display: "flex", alignItems: "center", gap: 8 }}>
          <span className="pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: C.signal, display: "block", flexShrink: 0 }} />
          {progress}
        </div>
      )}
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
        <Btn onClick={close}>Cancel</Btn>
        <Btn primary fullWidth onClick={submit} disabled={!file || uploading}>
          {uploading ? "AI processing…" : "Upload & Extract with AI"}
        </Btn>
      </div>
    </Modal>
  );
}

function Meta({ label, value, highlight, mono }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600, marginBottom: 3 }}>{label}</div>
      <div className={mono ? "mono" : ""} style={{ fontSize: 12, fontWeight: 500, color: highlight ? C.red : C.ink }}>{value}</div>
    </div>
  );
}

function EmptyState({ msg }) {
  return <div style={{ padding: "60px 20px", color: C.muted, fontSize: 13, textAlign: "center" }}>{msg}</div>;
}

// ─── GST Page ───────────────────────────────────────────────────────────
function GSTPage({ bp, notify }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [period, setPeriod] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
  });

  useEffect(() => {
    api.listCAClients().then((r) => setClients((r?.clients || []).map(c => ({...c, name: c.client_name || c.name || c.gstin})))).finally(() => setLoading(false));
  }, []);

  const reconcile = async (c) => {
    if (!c.gstin) { notify("No GSTIN for this client", "warn"); return; }
    if (!c.name) { notify("Client name missing", "warn"); return; }
    setRunning(c.gstin);
    try {
      const r = await api.runGSTReconciliation(c.name, c.gstin, period);
      if (r?.error) { notify(r.error, "warn"); }
      else notify(`Reconciled ${c.name}: ₹${((r?.total_itc_at_risk||0)/100000).toFixed(1)}L ITC at risk`);
    } catch (e) { notify(e.message || "Reconcile failed", "error"); }
    setRunning(null);
  };

  return (
    <div className="fade" style={{ padding: bp.mob ? 14 : "18px 22px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ background: C.surface, borderRadius: 7, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 600 }}>GST / ITC Reconciliation</h3>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Runs GSTR-2A/2B vs 3B matching for each client</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <label style={{ fontSize:11, color:C.muted }}>Period:</label>
            <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
              style={{ fontSize:12, padding:"4px 8px", border:`1px solid ${C.border}`, borderRadius:4, background:C.surface, fontFamily:"'JetBrains Mono',monospace" }}/>
          </div>
        </div>
        {loading && <LoadingRows count={3} />}
        {!loading && clients.filter((c) => c.gstin).map((c, i) => (
          <div key={c.id} style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
              <div className="mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{c.gstin}</div>
            </div>
            <Btn onClick={() => reconcile(c)} disabled={running === c.gstin}>
              {running === c.gstin ? "Running…" : "Reconcile"}
            </Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ITR Page ───────────────────────────────────────────────────────────
function ITRPage({ bp, notify }) {
  const [clients, setClients] = useState([]);
  const [running, setRunning] = useState(null);

  useEffect(() => { api.listCAClients().then((r) => setClients((r?.clients || []).map(c => ({...c, name: c.client_name || c.name || c.gstin})))); }, []);

  const prepare = async (c) => {
    setRunning(c.pan);
    try {
      if (!c.pan) { notify("PAN missing for " + c.name, "warn"); setRunning(null); return; }
      const r = await api.prepareITR(c.name, c.pan, "2025-26", "2024-25");
      notify(`ITR prepared for ${c.name} · Total tax ₹${r?.total_tax || "—"}`);
    } catch (e) { notify(e.message, "error"); }
    setRunning(null);
  };

  return (
    <div className="fade" style={{ padding: bp.mob ? 14 : "18px 22px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ background: C.surface, borderRadius: 7, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
          <h3 style={{ fontSize: 13, fontWeight: 600 }}>ITR Preparation — AY 2025-26</h3>
        </div>
        {clients.map((c) => (
          <div key={c.id} style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
              <div className="mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{c.pan} · {c.entity_type}</div>
            </div>
            <Btn onClick={() => prepare(c)} disabled={running === c.pan}>
              {running === c.pan ? "Preparing…" : "Prepare"}
            </Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Compliance Page (live SSE) ─────────────────────────────────────────
function CompliancePage({ bp, notify }) {
  const [cal, setCal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stream, setStream] = useState([]);

  useEffect(() => {
    api.getComplianceCalendar().then((r) => { try { let sc = r?.structured_calendar || {}; if (typeof sc === "string") sc = JSON.parse(sc); setCal([...(sc.critical||[]), ...(sc.urgent||[]), ...(sc.this_month||[])]); } catch { setCal([]); } }).finally(() => setLoading(false));
    const conn = api.connectComplianceStream(
      (msg) => setStream((s) => [msg, ...s].slice(0, 20)),
      (err) => {},
    );
    return () => conn?.close?.();
  }, []);

  return (
    <div className="fade" style={{ padding: bp.mob ? 14 : "18px 22px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: bp.tab ? "1fr" : "1fr 320px", gap: 14 }}>
        <div style={{ background: C.surface, borderRadius: 7, border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
            <h3 style={{ fontSize: 13, fontWeight: 600 }}>Filing Calendar</h3>
          </div>
          {loading && <LoadingRows count={4} />}
          {!loading && cal.map((d, i) => (
            <div key={i} style={{ padding: "12px 14px", borderBottom: i < cal.length - 1 ? `1px solid ${C.border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 3, height: 28, borderRadius: 2, background: d.urgent ? C.red : C.borderStrong }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{d.form_type || d.label}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{d.clients_affected || ""} · Due {d.due_date}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: C.night, borderRadius: 7, overflow: "hidden", color: "#D4CFBF" }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,.08)", display: "flex", alignItems: "center", gap: 8 }}>
            <span className="pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: C.signal }} />
            <span style={{ fontSize: 11, color: C.signal, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>Live stream</span>
          </div>
          <div style={{ padding: "8px 0", maxHeight: 400, overflowY: "auto" }}>
            {stream.length === 0 ? (
              <div style={{ padding: "20px 14px", fontSize: 11, color: "#857F72", textAlign: "center" }}>Waiting for events…</div>
            ) : stream.map((s, i) => (
              <div key={i} style={{ padding: "6px 14px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
                <span style={{ color: "#857F72" }}>[{new Date().toLocaleTimeString()}]</span> {JSON.stringify(s).slice(0, 80)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Documents Page ─────────────────────────────────────────────────────
function DocumentsPage({ bp, notify }) {
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("engagement_letter");
  const [clientId, setClientId] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => { api.listCAClients().then((r) => setClients((r?.clients || []).map(c => ({...c, name: c.client_name || c.name || c.gstin})))); }, []);

  const selected = clients.find((c) => (c.pan && c.pan === clientId) || (c.gstin && c.gstin === clientId));

  const tabs = [
    { id: "engagement_letter", label: "Engagement Letter", backendType: "Engagement Letter" },
    { id: "computation_sheet", label: "Computation Sheet", backendType: "Computation Sheet" },
    { id: "form_3cd",          label: "Form 3CD",          backendType: "Form 3CD" },
  ];

  const generate = async () => {
    if (!selected) { notify("Select a client", "warn"); return; }
    setGenerating(true);
    try {
      // Map UI tab id → backend document_type string
      const typeMap = {
        engagement_letter: "Engagement Letter",
        computation_sheet: "Computation Sheet",
        form_3cd: "Form 3CD",
      };
      const r = await api.generateDocument(
        selected.name, selected.pan, "2024-25", "2025-26", typeMap[tab] || tab
      );
      notify(`Downloaded: ${r.filename}`);
    } catch (e) { notify(e.message || "Generation failed", "error"); }
    setGenerating(false);
  };

  return (
    <div className="fade" style={{ padding: bp.mob ? 14 : "18px 22px", maxWidth: 900, margin: "0 auto" }}>
      <div style={{ background: C.surface, borderRadius: 7, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 6, overflowX: "auto" }}>
          {tabs.map((t) => (
            <Filter key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</Filter>
          ))}
        </div>
        <div style={{ padding: 18 }}>
          <label style={{ display: "block", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: C.ink2, fontWeight: 500, marginBottom: 4 }}>Client *</div>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={inputStyle}>
              <option value="">Select…</option>
              {clients.map((c) => <option key={c.pan || c.gstin} value={c.pan || c.gstin}>{c.name || c.pan || c.gstin}</option>)}
            </select>
          </label>
          <Btn primary fullWidth onClick={generate} disabled={!selected || generating}>
            {generating ? "Generating…" : `Generate ${tabs.find((t) => t.id === tab)?.label}`}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Ask Page (wired to /search/ask) ────────────────────────────────────
function AskPage({ bp }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), [msgs, pending]);

  const send = async () => {
    if (!input.trim() || pending) return;
    const q = input.trim();
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setPending(true);
    try {
      const r = await api.quickAsk(q);
      const answer = r?.answer || r?.response || "";
      const sources = r?.sources || [];
      const confidence = r?.confidence || 0;
      let text = answer;
      if (!text || confidence === 0 || sources.length === 0) {
        text = answer ||
          "No data found for this query. This usually means no data connectors are connected yet. " +
          "Go to Settings → Data sources and connect Tally, GST Portal, or IT Portal. " +
          "Once connected and synced, Ask CA Assist will search across your actual client data.";
      }
      setMsgs((m) => [...m, { role: "ai", text, lowConfidence: confidence < 0.3 && sources.length === 0 }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "ai", text: "Error: " + e.message, error: true }]);
    }
    setPending(false);
  };

  const suggestions = [
    "Which clients have ITC mismatches above ₹1 lakh?",
    "Show all pending notices with deadlines",
    "Who hasn't filed GSTR-3B for December?",
    "Draft notice reply for Verma Pharma 143(1)",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", maxWidth: 780, margin: "0 auto" }}>
      {msgs.length === 0 ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: `linear-gradient(135deg, ${C.signal}, ${C.signalDeep})`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, color: "#fff", fontSize: 22 }}>✦</div>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Ask CA Assist</h2>
          <div style={{ fontSize: 13, color: C.muted, textAlign: "center", maxWidth: 400, marginBottom: 24 }}>
            Search across Tally, GST, IT Portal, TRACES, MCA. Plain English.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: bp.mob ? "1fr" : "1fr 1fr", gap: 8, width: "100%", maxWidth: 560 }}>
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => setInput(s)} style={{
                padding: "10px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
                textAlign: "left", fontSize: 12, color: C.ink, lineHeight: 1.5,
              }}>{s}</button>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          {msgs.map((m, i) => (
            <div key={i} className="fade" style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap: 8 }}>
              {m.role === "ai" && <div style={{ width: 26, height: 26, borderRadius: 7, background: `linear-gradient(135deg, ${C.signal}, ${C.signalDeep})`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, flexShrink: 0 }}>✦</div>}
              <div style={{
                maxWidth: "85%", padding: "10px 14px", borderRadius: 10,
                background: m.role === "user" ? C.ink : m.error ? C.redBg : C.surface,
                color: m.role === "user" ? "#fff" : m.error ? C.redDeep : C.ink,
                border: m.role === "ai" && !m.error ? `1px solid ${C.border}` : "none",
                fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap",
              }}>{m.text}</div>
            </div>
          ))}
          {pending && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: `linear-gradient(135deg, ${C.signal}, ${C.signalDeep})`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>✦</div>
              <div style={{ padding: "10px 14px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, display: "flex", gap: 4 }}>
                {[0, 1, 2].map((i) => (
                  <span key={i} className="pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: C.muted, animationDelay: `${i * 0.2}s` }} />
                ))}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}
      <div style={{ padding: 14, borderTop: `1px solid ${C.border}`, background: C.surface }}>
        <div style={{ display: "flex", gap: 8, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px" }}>
          <textarea value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about clients, filings, ITC…" rows={1}
            style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 13, resize: "none", fontFamily: "inherit", color: C.ink }} />
          <button onClick={send} disabled={!input.trim() || pending} style={{
            width: 30, height: 30, borderRadius: 6, background: input.trim() ? C.ink : C.border, color: "#fff",
            fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
          }}>↑</button>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Page ──────────────────────────────────────────────────────
function SettingsPage({ bp, notify }) {
  const [connectors, setConnectors] = useState([]);
  const [wa, setWa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [tallyModalOpen, setTallyModalOpen] = useState(false);
  const [gstModalOpen, setGstModalOpen] = useState(false);
  const [itModalOpen, setItModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([
        api.listConnectors().catch(() => ({ connectors: [] })),
        api.getWhatsAppStatus().catch(() => null),
      ]);
      setConnectors(c?.connectors || []);
      setWa(w);
    } catch (e) { notify(e.message, "error"); }
    setLoading(false);
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const infos = {
    tally: { name: "Tally Prime",          desc: "Vouchers, ledgers, GST fields", ico: "📘" },
    gst_portal: { name: "GST Portal",      desc: "GSTR-1, 2A, 2B, 3B via GSP",     ico: "🏛️" },
    it_portal: { name: "Income Tax Portal",desc: "26AS, AIS, ITR, notices",       ico: "🪪" },
    traces: { name: "TRACES",              desc: "24Q, 26Q, Form 16",             ico: "📋" },
    mca21: { name: "MCA21",                desc: "Company master, ROC filings",   ico: "🏢" },
    bank_statement: { name: "Bank",        desc: "PDF/Excel statement parser",    ico: "🏦" },
    whatsapp_business: { name: "WhatsApp", desc: "Reminders + notice alerts",     ico: "💬" },
  };

  const connectedTypes = new Set((connectors || []).map((c) => c.connector_type));

  return (
    <div className="fade" style={{ padding: bp.mob ? 14 : "18px 22px", maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Data sources</h2>
        <div style={{ fontSize: 12, color: C.muted }}>Connect the tools your clients already use. CA Assist reads — never writes.</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: bp.mob ? "1fr" : "repeat(2, 1fr)", gap: 11 }}>
        {Object.entries(infos).map(([key, info]) => {
          const connected = connectedTypes.has(key) || (key === "whatsapp_business" && wa?.connected);
          return (
            <div key={key} style={{
              background: C.surface, borderRadius: 7, padding: "14px 16px",
              border: `1px solid ${connected ? C.signal + "60" : C.border}`,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 7, background: C.surfaceAlt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{info.ico}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{info.name}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{info.desc}</div>
                </div>
                <div style={{ padding: "2px 7px", borderRadius: 12, fontSize: 10, fontWeight: 600, background: connected ? C.signalBg : C.surfaceAlt, color: connected ? C.signalDeep : C.muted, whiteSpace: "nowrap" }}>
                  {connected ? "● Connected" : "○ Not set"}
                </div>
              </div>
              <Btn fullWidth onClick={() => {
                if (key === "whatsapp_business") setWaModalOpen(true);
                else if (key === "tally") setTallyModalOpen(true);
                else if (key === "gst_portal") setGstModalOpen(true);
                else if (key === "it_portal") setItModalOpen(true);
                else if (key === "traces") setItModalOpen(true);
                else notify(`${info.name} — configure via Settings → contact support`, "warn");
              }}>
                {connected ? "Manage" : "Connect"}
              </Btn>
            </div>
          );
        })}
      </div>

      {waModalOpen  && <WhatsAppSetupModal close={() => { setWaModalOpen(false);  load(); }} notify={notify} bp={bp} />}
      {tallyModalOpen && <TallySetupModal   close={() => { setTallyModalOpen(false); load(); }} notify={notify} bp={bp} />}
      {gstModalOpen  && <GSTPortalSetupModal close={() => { setGstModalOpen(false); load(); }} notify={notify} bp={bp} />}
      {itModalOpen   && <ITPortalSetupModal  close={() => { setItModalOpen(false);  load(); }} notify={notify} bp={bp} />}
    </div>
  );
}

function WhatsAppSetupModal({ close, notify, bp }) {
  const [token, setToken] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.setupWhatsApp({ access_token: token, phone_number_id: phoneId, display_name: displayName });
      notify("WhatsApp connected");
      close();
    } catch (e) {
      notify(e.message || "Setup failed", "error");
      setSaving(false);
    }
  };

  return (
    <Modal title="WhatsApp Business setup" close={close} bp={bp}>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 16, lineHeight: 1.6 }}>
        From Meta Business Manager: System User access token + phone number ID.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="Access token *"    v={token}       set={setToken}       ph="EAAB..." mono />
        <Field label="Phone Number ID *" v={phoneId}     set={setPhoneId}     ph="104825..." mono />
        <Field label="Display name"      v={displayName} set={setDisplayName} ph="GPS & Associates" />
      </div>
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
        <Btn onClick={close}>Cancel</Btn>
        <Btn primary fullWidth onClick={submit} disabled={!token || !phoneId || saving}>
          {saving ? "Validating…" : "Validate & Connect"}
        </Btn>
      </div>
    </Modal>
  );
}

// ─── Tally Setup Modal ───────────────────────────────────────────────────────
function TallySetupModal({ close, notify, bp }) {
  const [url, setUrl] = useState("http://");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const test = async () => {
    setTesting(true);
    try {
      const r = await fetch("/api/v1/ca/whatsapp/test", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("cosfy_access")}` },
        body: JSON.stringify({ tally_url: url }),
      }).catch(() => null);
      // Tally test: just try connecting — if it saves it will sync on first run
      notify("Test not available — save and check sync status after connecting");
    } catch (e) { notify("Test failed: " + e.message, "error"); }
    setTesting(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const me = await api.getMe();
      const resp = await fetch("/api/v1/connectors/tally/connect/apikey", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("cosfy_access")}` },
        body: JSON.stringify({ org_id: me.org_id, auth_config: { tally_url: url }, name: "Tally Prime" }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      notify("Tally connector saved — sync will start shortly");
      close();
    } catch (e) { notify(e.message || "Save failed", "error"); setSaving(false); }
  };

  return (
    <Modal title="Connect Tally Prime" close={close} bp={bp}>
      <div style={{ fontSize: 12, color: C.ink3, marginBottom: 14, lineHeight: 1.6, background: C.surfaceAlt, padding: "10px 12px", borderRadius: 6 }}>
        <strong>On the Tally machine:</strong> Gateway of Tally → F12 → Advanced Config → Enable HTTP Server → Port: 9000 → Accept
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="Tally HTTP URL *" v={url} set={setUrl} ph="http://192.168.1.10:9000" mono />
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          Use the LAN IP of the Tally machine. If accessing remotely, install Tailscale on the Tally machine and use its 100.x.x.x address.
        </div>
      </div>
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
        <Btn onClick={close}>Cancel</Btn>
        <Btn onClick={test} disabled={testing || !url.startsWith("http")}>{testing ? "Testing…" : "Test connection"}</Btn>
        <Btn primary fullWidth onClick={save} disabled={saving || !url.startsWith("http")}>{saving ? "Saving…" : "Save & Sync"}</Btn>
      </div>
    </Modal>
  );
}

// ─── GST Portal Setup Modal ──────────────────────────────────────────────────
function GSTPortalSetupModal({ close, notify, bp }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [gstins, setGstins] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const gstin_list = gstins.split(/[\n,\s]+/).map(g => g.trim().toUpperCase()).filter(Boolean);
      if (gstin_list.length === 0) { notify("Add at least one GSTIN", "warn"); setSaving(false); return; }
      const me2 = await api.getMe();
      const resp = await fetch("/api/v1/connectors/gst_portal/connect/apikey", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("cosfy_access")}` },
        body: JSON.stringify({ org_id: me2.org_id, auth_config: { username, password, gstin_list }, name: "GST Portal" }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      notify(`GST Portal connected — ${gstin_list.length} GSTIN(s) queued for sync`);
      close();
    } catch (e) { notify(e.message || "Setup failed", "error"); setSaving(false); }
  };

  return (
    <Modal title="Connect GST Portal" close={close} bp={bp}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
        Use a GSP-enabled login. CA Assist reads only — no filings, no changes.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="GST Portal username" v={username} set={setUsername} ph="AABCV7823H" />
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, color: C.ink2, fontWeight: 500, marginBottom: 4 }}>Password</div>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="••••••••" style={inputStyle} />
        </label>
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, color: C.ink2, fontWeight: 500, marginBottom: 4 }}>GSTINs to sync (one per line or comma-separated)</div>
          <textarea value={gstins} onChange={e => setGstins(e.target.value)}
            placeholder={"27AABCS1429B1ZS\n08AABCV7823H1ZX"}
            rows={4} style={{ ...inputStyle, resize: "vertical" }} />
        </label>
      </div>
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
        <Btn onClick={close}>Cancel</Btn>
        <Btn primary fullWidth onClick={save} disabled={saving || !username}>{saving ? "Saving…" : "Connect & Sync"}</Btn>
      </div>
    </Modal>
  );
}

// ─── IT Portal / TRACES Setup Modal ──────────────────────────────────────────
function ITPortalSetupModal({ close, notify, bp }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pans, setPans] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const pan_list = pans.split(/[\n,\s]+/).map(p => p.trim().toUpperCase()).filter(Boolean);
      if (pan_list.length === 0) { notify("Add at least one PAN", "warn"); setSaving(false); return; }
      const me3 = await api.getMe();
      const resp = await fetch("/api/v1/connectors/it_portal/connect/apikey", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("cosfy_access")}` },
        body: JSON.stringify({ org_id: me3.org_id, auth_config: { username, password, pan_list }, name: "IT Portal" }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      notify(`IT Portal connected — ${pan_list.length} PAN(s) queued for sync`);
      close();
    } catch (e) { notify(e.message || "Setup failed", "error"); setSaving(false); }
  };

  return (
    <Modal title="Connect Income Tax Portal" close={close} bp={bp}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
        Use your CA's IT Portal login (ERI or direct). CA Assist pulls 26AS, AIS, ITR status, notices.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Field label="IT Portal PAN / username" v={username} set={setUsername} ph="AAAPG1234A" />
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, color: C.ink2, fontWeight: 500, marginBottom: 4 }}>Password</div>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="••••••••" style={inputStyle} />
        </label>
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, color: C.ink2, fontWeight: 500, marginBottom: 4 }}>Client PANs to sync (one per line or comma-separated)</div>
          <textarea value={pans} onChange={e => setPans(e.target.value)}
            placeholder={"AABCV7823H\nAABCS1429B"}
            rows={4} style={{ ...inputStyle, resize: "vertical" }} />
        </label>
      </div>
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
        <Btn onClick={close}>Cancel</Btn>
        <Btn primary fullWidth onClick={save} disabled={saving || !username}>{saving ? "Saving…" : "Connect & Sync"}</Btn>
      </div>
    </Modal>
  );
}
